"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Check, X, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { slideVariants, fadeInVariants } from "@/utils/animation"

type PlanCell =
    | { type: "check" }
    | { type: "cross" }
    | { type: "number"; value: number | string }

interface FeatureRow {
    id: string
    label: string
    expandable?: boolean
    subItems?: string[]
    cells: [PlanCell, PlanCell, PlanCell] // Basic, Pro, Premium
}

const check: PlanCell = { type: "check" }
const cross: PlanCell = { type: "cross" }

const PLAN_LABELS = ["Basic", "Pro", "Premium"] as const

const FEATURES: FeatureRow[] = [
    {
        id: "all-invitation-feature",
        label: "All Invitation Feature",
        expandable: true,
        cells: [check, check, check],
        subItems: [
            "Bebas ganti ke semua tema",
            "Ubah nama tamu unlimited",
            "Tanpa masa aktif",
            "RSVP & ucapan",
            "Terintegrasi Google Maps",
            "Sebar ke unlimited penerima",
            "Countdown menuju hari-H",
            "Pengingat Google Calendar",
            "Foto galeri & video",
            "Rekening titip hadiah",
            "Titip kado fisik ke acara",
            "Amplop digital di undangan",
            "Ratusan musik bisa custom",
            "Link live streaming",
            "Fitur auto scroll",
            "Ubah font dan warna tulisan",
            "Kustomisasi tema undangan",
            "QR code buku tamu",
            "Layar sapa & check-in tamu",
            "Love story & susunan acara",
            "Fitur turut mengundang",
            "Laporan statistik sebar",
            "Embed map lokasi acara",
            "Balas ucapan tamu",
            "Unlimited revisi sepuasnya",
            "Ubah susunan komponen",
        ],
    },
    { id: "unlimited-share", label: "Unlimited Share", cells: [check, check, check] },
    { id: "unlimited-contact", label: "Unlimited Contact", cells: [check, check, check] },
    { id: "gift-registry", label: "Gift Registry", cells: [check, check, check] },
    { id: "auto-amplop-digital", label: "Auto Amplop Digital", cells: [check, check, check] },
    { id: "auto-share-whatsapp", label: "Auto Share Whatsapp", cells: [check, check, check] },
    { id: "all-template", label: "All Template", cells: [check, check, check] },
    {
        id: "foto-media",
        label: "Foto Media",
        cells: [
            { type: "number", value: 25 },
            { type: "number", value: 50 },
            { type: "number", value: 200 },
        ],
    },
    { id: "rsvp-notification", label: "RSVP Notification", cells: [cross, check, check] },
    { id: "auto-followup-rsvp", label: "Auto Followup RSVP", cells: [cross, check, check] },
    { id: "custom-localization", label: "Custom Localization", cells: [cross, check, check] },
    { id: "custom-form-rsvp", label: "Custom Form RSVP", cells: [cross, check, check] },
    { id: "vip-support", label: "VIP Support", cells: [cross, check, check] },
    { id: "collaboration", label: "Collaboration", cells: [cross, cross, check] },
    { id: "unlimited-auto-share-email", label: "Unlimited Auto Share Email", cells: [cross, cross, check] },
    { id: "video-background", label: "Video Background", cells: [cross, cross, check] },
    { id: "custom-design", label: "Custom Design", cells: [cross, cross, check] },
    { id: "online-meeting", label: "Online Meeting", cells: [cross, cross, check] },
]

const INITIAL_VISIBLE = 8
const INITIAL_FEATURES = FEATURES.slice(0, INITIAL_VISIBLE)
const EXTRA_FEATURES = FEATURES.slice(INITIAL_VISIBLE)

const stickyFiturCellClass =
    "sticky left-0 z-10 flex items-center bg-white py-3.5 pr-3 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.08)]"

function PlanCellDisplay({ cell }: { cell: PlanCell }) {
    if (cell.type === "check") {
        return (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#E4F6EC] text-[#1FAA5A]">
                <Check size={13} strokeWidth={3} />
            </span>
        )
    }
    if (cell.type === "cross") {
        return (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#F1F2F4] text-[#9AA0AA]">
                <X size={13} strokeWidth={3} />
            </span>
        )
    }
    return (
        <span className="font-[Poppins,sans-serif] text-[13px] font-semibold text-[#2E2E38] sm:text-[14px]">
            {cell.value}
        </span>
    )
}

function FeatureRowItem({
    feature,
    isLast,
    isSubOpen,
    onToggleSubList,
}: {
    feature: FeatureRow
    isLast: boolean
    isSubOpen: boolean
    onToggleSubList: () => void
}) {
    return (
        <div className="border-t border-[#EEF0F3]">
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr] items-stretch gap-2 px-2 sm:gap-0 sm:px-[26px]">
                {feature.expandable ? (
                    <button
                        onClick={onToggleSubList}
                        aria-expanded={isSubOpen}
                        className={cn(
                            stickyFiturCellClass,
                            "gap-2 text-left text-[13px] text-[#3A3A45] sm:text-[15px]"
                        )}
                    >
                        <span className="underline decoration-dashed decoration-[#C7CAD0] underline-offset-4">
                            {feature.label}
                        </span>
                        <ChevronRight
                            size={16}
                            className={cn(
                                "shrink-0 text-[#9AA0AA] transition-transform duration-300",
                                isSubOpen && "rotate-90"
                            )}
                        />
                    </button>
                ) : (
                    <span
                        className={cn(
                            stickyFiturCellClass,
                            "text-[13px] text-[#3A3A45] sm:text-[15px]"
                        )}
                    >
                        {feature.label}
                    </span>
                )}

                {feature.cells.map((cell, cellIndex) => (
                    <span
                        key={cellIndex}
                        className={cn(
                            "flex items-center justify-center py-3.5",
                            cellIndex === 2 &&
                            cn("bg-[#F4F5F7]", isLast && "rounded-b-2xl")
                        )}
                    >
                        <PlanCellDisplay cell={cell} />
                    </span>
                ))}
            </div>

            {/* Sub-list (khusus baris expandable) */}
            {feature.subItems && (
                <div
                    className={cn(
                        "grid transition-all duration-300 ease-in-out",
                        isSubOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    )}
                >
                    <div className="overflow-hidden">
                        <div
                            className={cn(
                                "sticky left-0 grid w-[calc(100vw-2rem)] max-w-[560px] grid-cols-1 gap-1.5 px-2 pb-4 pt-1",
                                "sm:w-auto sm:max-w-none sm:grid-cols-2 sm:gap-x-6 sm:gap-y-1.5 sm:px-[26px]"
                            )}
                        >
                            {feature.subItems.map((item) => (
                                <span
                                    key={item}
                                    className="flex items-start gap-2.5 text-[13px] text-[#5A5A66] sm:text-[14px]"
                                >
                                    <span className="font-bold leading-[1.2] text-[#ED6A57]">•</span>
                                    {item}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default function ComparisonTable() {
    const [expanded, setExpanded] = useState(false)
    const [openSubList, setOpenSubList] = useState<string | null>(null)
    const hasMore = EXTRA_FEATURES.length > 0

    return (
        <section className="bg-white px-4 py-14 sm:px-6 md:py-20 ">
            <div className="max-w-7xl mx-auto text-center">
                <motion.div
                    variants={slideVariants(false, 0.6)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    <h3 className="mb-2 font-[Poppins,sans-serif] text-[22px] font-semibold text-[#2E2E38] sm:text-[26px]">
                        Perbandingan Lengkap Fitur
                    </h3>
                    <p className="mb-6 text-[14px] text-[#6B6B76] sm:mb-[30px] sm:text-[15px]">
                        Lihat detail apa saja yang tersedia di setiap paket.
                    </p>
                </motion.div>

                <motion.div
                    variants={fadeInVariants(0.5)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:overflow-visible sm:px-0"
                >
                    <div className="min-w-[560px]">

                        {/* Header — padding vertikal juga per-sel (py-2), konsisten
                        dengan pola baris di bawahnya */}
                        <div className="grid grid-cols-[2fr_1fr_1fr_1fr] items-stretch gap-2 px-2 sm:gap-0 sm:px-[26px]">
                            <span
                                className={cn(
                                    stickyFiturCellClass,
                                    "!py-2 text-left font-[Poppins,sans-serif] text-[11px] font-semibold uppercase tracking-wider text-[#9AA0AA] sm:text-[13px]"
                                )}
                            >
                                Fitur
                            </span>
                            {PLAN_LABELS.map((label, i) => (
                                <span
                                    key={label}
                                    className={cn(
                                        "flex items-center justify-center py-2 font-[Poppins,sans-serif] text-[13px] font-semibold sm:text-[15px]",
                                        i === 2 ? "rounded-t-2xl bg-[#F4F5F7] text-[#ED6A57]" : "text-[#2E2E38]"
                                    )}
                                >
                                    {label}
                                </span>
                            ))}
                        </div>

                        {/* Baris utama — selalu tampil */}
                        <div>
                            {INITIAL_FEATURES.map((feature, index) => (
                                <FeatureRowItem
                                    key={feature.id}
                                    feature={feature}
                                    isLast={!expanded && index === INITIAL_FEATURES.length - 1}
                                    isSubOpen={openSubList === feature.id}
                                    onToggleSubList={() =>
                                        setOpenSubList((prev) => (prev === feature.id ? null : feature.id))
                                    }
                                />
                            ))}
                        </div>

                        {hasMore && (
                            <div
                                className={cn(
                                    "grid transition-all duration-500 ease-in-out",
                                    expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                                )}
                            >
                                <div className={expanded ? "overflow-visible" : "overflow-hidden"}>
                                    {EXTRA_FEATURES.map((feature, index) => (
                                        <FeatureRowItem
                                            key={feature.id}
                                            feature={feature}
                                            isLast={index === EXTRA_FEATURES.length - 1}
                                            isSubOpen={openSubList === feature.id}
                                            onToggleSubList={() =>
                                                setOpenSubList((prev) => (prev === feature.id ? null : feature.id))
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>

                {hasMore && (
                    <div className="sticky bottom-4 z-20 mt-6 flex justify-center sm:bottom-6 sm:mt-8">
                        <button
                            onClick={() => setExpanded((prev) => !prev)}
                            className="rounded-xl border-[1.5px] border-[#D9DCE1] bg-white px-[30px] py-3 font-[Poppins,sans-serif] text-[14px] font-semibold text-[#2E2E38] shadow-[0_6px_18px_rgba(30,30,50,0.12)] transition-colors hover:border-[#9AA0AA] hover:scale-[1.03] sm:text-[15px]"
                        >
                            {expanded ? "Tutup" : "Lihat Semua Fitur"}
                        </button>
                    </div>
                )}
            </div>
        </section>
    )
}